/*
 * Copyright (c) 2023 SARL SIGMA POLYNESIA
 */

import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl'
import FormattedFileSize from '../../../../client/js/components/FormattedFileSize'
import { PropsWithChildren, useCallback, useState } from 'react'
import { selectFile } from '../../../../client/js/lib/download'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import DeleteModal from '../../../../client/js/components/modals/DeleteModal'
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload'
import { Box, Button, Group, Text } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'

export type FileDropZoneProps = PropsWithChildren & {
  accept?: string[],
  contentLength?: number,
  disabled?: boolean,
  error?: Error,
  label?: string,
  lastModified?: string,
  loading?: boolean,
  maxSize?: number,
  onChange? (file: File): void,
  onDelete? (): Promise<unknown>,
  placeholder?: string,
  url?: string,
  value?: string,
}

function FileDropZone (props: FileDropZoneProps) {
  const {
    accept,
    children,
    contentLength,
    disabled,
    error,
    label,
    lastModified,
    loading,
    maxSize,
    onChange,
    onDelete,
    placeholder,
    url,
    value,
    ...others
  } = props

  const intl = useIntl()

  const [isDelete, setIsDelete] = useState<boolean>(false)
  const [isDragging, setIsDragging] = useState(false)

  const handleDragEnter = useCallback(() => {
    setIsDragging(true)
  }, [])

  const handleDragExit = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    setIsDragging(false)

    // Process only the first file.
    if (onChange && event.dataTransfer.files.length > 0) {
      onChange(event.dataTransfer.files[0])
    }
  }, [onChange])

  const handleFileSelect = useCallback(() => {
    selectFile(onChange, { accept: accept?.join(',') })
  }, [accept, onChange])

  return (
    <Box
      bg={isDragging ? 'blue' : undefined}
      {...others}
      onDragEnter={handleDragEnter}
      onDragExit={handleDragExit}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Group align="start">
        <div>
          <Text>{label}</Text>
          {value != null && url != null
            ? <AnchorLink
              to={url}
              rel="noreferrer"
              target="_blank"
            >
              {value}
            </AnchorLink>
            : value || placeholder}

          {lastModified != null
            ? <Text c="dimmed" size="sm">
              <FormattedMessage
                id="uploaded_at"
                defaultMessage="Transféré le {date} à {time}"
                values={{
                  date: <FormattedDate value={lastModified} />,
                  time: <FormattedTime value={lastModified} />
                }}
              />
            </Text>
            : accept != null && accept.length > 0
              ? <Text c="dimmed" size="sm">
                {intl.formatMessage({
                  id: 'accepted_file_formats_%',
                  defaultMessage: 'Formats acceptés : {formats}'
                }, {
                  formats: intl.formatList(accept)
                })}
              </Text>
              : null}

          {contentLength >= 0
            ? <Text c="dimmed" size="sm">
              <FormattedFileSize value={contentLength} />
            </Text>
            : maxSize > 0
              ? <Text c="dimmed" size="sm">
                {intl.formatMessage({
                  id: 'max_file_size_%',
                  defaultMessage: 'Taille maximale : {maxSize}'
                }, {
                  maxSize: <FormattedFileSize value={maxSize} />
                })}
              </Text>
              : null}

          {error != null
            ? <Text c="red" size="sm">
              {error.message}
            </Text>
            : null}
        </div>
        <Box ms="auto">
          {onDelete != null && (lastModified != null || contentLength != null)
            ? <Button
              variant="default"
              disabled={disabled || loading}
              loading={loading}
              onClick={() => {
                setIsDelete(true)
              }}
              size="xs"
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
            : <Button
              variant="default"
              disabled={disabled || loading || onChange == null}
              loading={loading}
              onClick={handleFileSelect}
              size="xs"
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faUpload}
              />
            </Button>
          }
        </Box>
      </Group>
      {children}

      <DeleteModal
        opened={isDelete}
        label={value}
        onConfirm={onDelete}
        onClose={() => {
          setIsDelete((s) => !s)
        }}
      />
    </Box>
  )
}

export default FileDropZone
